frappe.provide("erpnext.ui");

/**
 * Data Service to handle API interactions
 */
class PricingDataService {
	static async getPriceLists(doctype, doc, start = 0) {
		let filters = { enabled: 1 };
		if (doctype === "Supplier") filters.buying = 1;
		if (doctype === "Customer") filters.selling = 1;
		if (doctype == "Item") {
			if (doc.is_purchase_item && !doc.is_sales_item) {
				filters.buying = 1;
			} else if (!doc.is_purchase_item && doc.is_sales_item) {
				filters.selling = 1;
			}
		}

		const res = await frappe.call({
			method: "frappe.client.get_list",
			args: {
				doctype: "Price List",
				filters: filters,
				fields: ["name", "currency", "buying", "selling"],
				limit_start: start,
				limit_page_length: 20,
			},
		});
		return res.message || [];
	}

	static async getPrices(doctype, docName, itemCode) {
		// @dokos: use the whitelisted, permission-checked endpoint instead of an unrestricted
		// client query. It applies the Item/Supplier/Customer filters server-side and excludes
		// expired/cancelled prices.
		const response = await frappe.call({
			method: "erpnext.stock.doctype.item.item.get_item_prices",
			args: { doctype: doctype, docname: docName, item_code: itemCode },
		});
		return (response.message && response.message.prices) || [];
	}
}

erpnext.ui.pricing_dashboard = class ItemPricingDashboard {
	constructor(frm, container) {
		this.frm = frm;
		this.container = container;
		this.doctype = frm.doc.doctype;
		this.price_lists = [];
		this.groupedPrices = {};
		this.can_delete = frappe.model.can_delete("Item Price");
		this.start = 0;
		this.init();
	}

	async init() {
		this.price_lists = [];
		this.groupedPrices = {};
		this.start = 0;
		await this.loadData();
	}

	async loadData() {
		// Fetch the next 20 Price Lists
		const newPriceLists = await PricingDataService.getPriceLists(this.doctype, this.frm.doc, this.start);

		if (newPriceLists.length > 0) {
			this.price_lists = this.price_lists.concat(newPriceLists);

			// Fetch prices for context (Note: You might want to filter this by newPriceLists)
			const allPrices = await PricingDataService.getPrices(
				this.doctype,
				this.frm.doc.name,
				this.frm.doc.item_code
			);
			this.processPrices(allPrices);

			this.start += 20;
			this.render();
		}
	}

	processPrices(prices) {
		const today = frappe.datetime.get_today();
		const cutoff = frappe.datetime.add_days(today, -30);

		const filtered = prices.filter((p) => !p.valid_upto || p.valid_upto >= cutoff);

		this.groupedPrices = filtered.reduce((acc, curr) => {
			if (!acc[curr.price_list]) acc[curr.price_list] = [];
			acc[curr.price_list].push(curr);
			return acc;
		}, {});
	}

	render() {
		const sections = this.price_lists
			.map((pl) => {
				const prices = this.groupedPrices[pl.name] || [];
				return this.getSectionHtml(pl, prices);
			})
			.join("");

		// Show "More" only if the current count is a multiple of 20
		const showMore = this.price_lists.length > 0 && this.price_lists.length % 20 === 0;

		this.container.innerHTML = `
			<div class="dk-pricing-wrapper">
				${sections}
				${
					showMore
						? `
					<div class="text-center margin-top">
						<button class="btn btn-default btn-sm btn-load-more">
							${__("More")}...
						</button>
					</div>`
						: ""
				}
			</div>`;

		this.bindEvents();
	}

	getSectionHtml(pl, prices) {
		const hasPrices = prices.length > 0;
		const dimensionHeader = this.doctype === "Item" ? __("Partner / Batch") : __("Item");

		return `
			<div class="dk-price-group">
				${this.getHeaderHtml(pl, hasPrices)}
				<table class="dk-table">
					<thead>
						<tr>
							<th class="dk-col-details"></th>
							<th>${dimensionHeader}</th>
							<th class="text-right">${__("Rate")}</th>
							<th class="text-center">${__("Valid From")}</th>
							<th class="text-center">${__("Valid Upto")}</th>
							<th class="text-right">${__("Actions")}</th>
						</tr>
					</thead>
					<tbody>
						${hasPrices ? prices.map((p) => this.getRowHtml(p)).join("") : this.getEmptyRowHtml()}
					</tbody>
				</table>
			</div>`;
	}

	getHeaderHtml(pl, hasPrices) {
		const plTypeLabel = pl.buying ? __("Buying") : __("Selling");
		return `
			<div class="dk-group-header">
				<div class="dk-header-left">
					<span class="dk-badge-pl">${pl.name}</span>
					<small class="text-muted ml-2">(${plTypeLabel})</small>
				</div>
				<div class="dk-header-actions">
					${hasPrices ? this.getBulkMenuHtml(pl.name) : ""}
					<button class="btn btn-xs btn-secondary btn-add-new-price" data-pl='${JSON.stringify(pl)}'>
						${frappe.utils.icon("add", "xs")} ${__("Add Price")}
					</button>
				</div>
			</div>`;
	}

	getBulkMenuHtml(plName) {
		return `
			<div class="dropdown inline-block">
				<button class="btn btn-xs btn-default dropdown-toggle" data-toggle="dropdown">
					${__("Bulk Actions")} <span class="caret"></span>
				</button>
				<ul class="dropdown-menu dropdown-menu-right">
					<li><a class="dropdown-item btn-bulk-adjust" data-type="increase" data-pl='${plName}'>${__(
			"Increase Prices (%)"
		)}</a></li>
					<li><a class="dropdown-item btn-bulk-adjust" data-type="decrease" data-pl='${plName}'>${__(
			"Decrease Prices (%)"
		)}</a></li>
					<li class="divider"></li>
					<li><a class="dropdown-item btn-bulk-close" data-pl='${plName}'>${__("Bulk Close Prices")}</a></li>
				</ul>
			</div>`;
	}

	getRowHtml(p) {
		const isFuture = p.valid_from > frappe.datetime.get_today();
		const dimensionLabel = this.getDimensionLabel(p);
		const rowData = encodeURIComponent(JSON.stringify(p));

		return `
			<tr class="${isFuture ? "is-future" : ""}" data-name="${p.name}" data-info="${rowData}">
				<td class="dk-col-details"><a class="btn-view-details">${frappe.utils.icon("info", "sm")}</a></td>
				<td class="dk-dimension-cell">${dimensionLabel}</td>
				<td class="dk-rate-cell text-right"><span class="dk-amount">${format_currency(
					p.price_list_rate,
					p.currency
				)}</span></td>
				<td class="text-center dk-date-cell">${frappe.datetime.str_to_user(p.valid_from)}</td>
				<td class="text-center dk-date-cell">${p.valid_upto ? frappe.datetime.str_to_user(p.valid_upto) : "--"}</td>
				<td class="text-right">${this.getRowActionsHtml()}</td>
			</tr>`;
	}

	getDimensionLabel(p) {
		if (this.doctype !== "Item") {
			return `<strong>${p.item_code}</strong> <br><small class="text-muted">${
				p.item_name || ""
			}</small>`;
		}
		const dims = [];
		if (p.supplier) dims.push(`<strong>${__("Supplier")}:</strong> ${p.supplier}`);
		if (p.customer) dims.push(`<strong>${__("Customer")}:</strong> ${p.customer}`);
		if (p.batch_no) dims.push(`<strong>${__("Batch")}:</strong> ${p.batch_no}`);
		return dims.length > 0 ? dims.join(" | ") : `<em>${__("Standard")}</em>`;
	}

	getRowActionsHtml() {
		return `
			<div class="dropdown">
				<button class="btn btn-xs btn-default dropdown-toggle" data-toggle="dropdown">${__(
					"Actions"
				)} <span class="caret"></span></button>
				<ul class="dropdown-menu dropdown-menu-right">
					<li><a class="dropdown-item btn-new-revision">${__("New Revision")}</a></li>
					<li><a class="dropdown-item btn-close-price">${__("Close Price")}</a></li>
					${
						this.can_delete
							? `<li class="divider"></li><li><a class="dropdown-item btn-delete-price text-danger">${__(
									"Delete"
							  )}</a></li>`
							: ""
					}
				</ul>
			</div>`;
	}

	getEmptyRowHtml() {
		return `<tr><td colspan="6" class="text-center text-muted p-4"><small>${__(
			"No active price entries for this list."
		)}</small></td></tr>`;
	}

	bindEvents() {
		const find = (selector) => this.container.querySelectorAll(selector);

		const moreBtn = this.container.querySelector(".btn-load-more");
		if (moreBtn) {
			moreBtn.onclick = async (e) => {
				const $btn = $(e.currentTarget);
				$btn.prop("disabled", true).text(__("Loading..."));
				await this.loadData();
			};
		}

		find(".btn-delete-price").forEach(
			(el) => (el.onclick = (e) => this.handleDelete(this.getRowData(e).name))
		);
		find(".btn-close-price").forEach(
			(el) => (el.onclick = (e) => this.handleClose(this.getRowData(e).name))
		);
		find(".btn-new-revision").forEach(
			(el) => (el.onclick = (e) => this.handleNewRevision(this.getRowData(e)))
		);
		find(".btn-view-details").forEach(
			(el) => (el.onclick = (e) => this.showDetailsDialog(this.getRowData(e)))
		);
		find(".btn-add-new-price").forEach(
			(el) => (el.onclick = (e) => this.handleAddNewPrice(JSON.parse(e.currentTarget.dataset.pl)))
		);
		find(".btn-bulk-adjust").forEach(
			(el) =>
				(el.onclick = (e) =>
					this.handleBulkAdjustment(e.currentTarget.dataset.type, e.currentTarget.dataset.pl))
		);
		find(".btn-bulk-close").forEach(
			(el) => (el.onclick = (e) => this.handleBulkClose(e.currentTarget.dataset.pl))
		);
	}

	getRowData(e) {
		return JSON.parse(decodeURIComponent(e.target.closest("tr").dataset.info));
	}

	showDetailsDialog(data) {
		const d = new frappe.ui.Dialog({
			title: `${__("Item Price Details")}: ${data.name}`,
			fields: [
				{
					label: __("Price List"),
					fieldname: "price_list",
					fieldtype: "Link",
					options: "Price List",
					read_only: 1,
					default: data.price_list,
				},
				{
					label: __("Supplier"),
					fieldname: "supplier",
					fieldtype: "Link",
					options: "Supplier",
					read_only: 1,
					default: data.supplier,
					depends_on: "eval:doc.supplier",
				},
				{
					label: __("Rate"),
					fieldname: "price_list_rate",
					fieldtype: "Currency",
					options: "currency",
					read_only: 1,
					default: data.price_list_rate,
				},
				{
					label: __("Currency"),
					fieldname: "currency",
					fieldtype: "Link",
					options: "Currency",
					read_only: 1,
					default: data.currency,
				},
				{ fieldtype: "Section Break" },
				{
					label: __("Valid From"),
					fieldname: "valid_from",
					fieldtype: "Date",
					read_only: 1,
					default: data.valid_from,
				},
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					read_only: 1,
					default: data.valid_upto,
				},
				{ fieldtype: "Section Break" },
				{
					label: __("UOM"),
					fieldname: "uom",
					fieldtype: "Link",
					options: "UOM",
					read_only: 1,
					default: data.uom,
				},
				{
					label: __("Packing Unit"),
					fieldname: "packing_unit",
					fieldtype: "Float",
					read_only: 1,
					default: data.packing_unit,
				},
			],
		});

		d.set_secondary_action_label(__("Open Full Form"));
		d.set_secondary_action(() => {
			frappe.set_route("Form", "Item Price", data.name);
		});

		d.show();
	}

	handleDelete(name) {
		frappe.confirm(__("Are you sure you want to delete this price permanently?"), () => {
			frappe.call({
				method: "frappe.client.delete",
				args: { doctype: "Item Price", name: name },
				callback: () => {
					frappe.show_alert({ message: __("Price deleted"), subtitle: "success" });
					this.init();
				},
			});
		});
	}

	handleClose(name) {
		const d = new frappe.ui.Dialog({
			title: __("Close Price"),
			fields: [
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.get_today(),
				},
			],
			primary_action_label: __("Close Now"),
			primary_action: (values) => {
				frappe.db.set_value("Item Price", name, "valid_upto", values.valid_upto).then(() => {
					d.hide();
					this.init();
				});
			},
		});
		d.show();
	}

	handleNewRevision(oldData) {
		const isSales = this.doctype === "Customer" || (this.doctype === "Item" && !oldData.buying);

		const d = new frappe.ui.Dialog({
			title: __("New Price Revision"),
			fields: [
				{
					label: __("New Rate"),
					fieldname: "price_list_rate",
					fieldtype: "Currency",
					reqd: 1,
					default: oldData.price_list_rate,
				},
				{
					label: __("Starts On"),
					fieldname: "valid_from",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.add_days(frappe.datetime.get_today(), 1),
				},
				{
					label: __("Close previous price?"),
					fieldname: "auto_close",
					fieldtype: "Check",
					default: 1,
				},
			],
			primary_action: async (values) => {
				if (values.auto_close) {
					await frappe.db.set_value(
						"Item Price",
						oldData.name,
						"valid_upto",
						frappe.datetime.add_days(values.valid_from, -1)
					);
				}

				const newDoc = {
					doctype: "Item Price",
					item_code: oldData.item_code,
					price_list: oldData.price_list,
					price_list_rate: values.price_list_rate,
					valid_from: values.valid_from,
					supplier: oldData.supplier,
					customer: oldData.customer,
					batch_no: oldData.batch_no,
					company: oldData.company,
					currency: oldData.currency,
					buying: isSales ? 0 : 1,
					selling: isSales ? 1 : 0,
				};

				frappe.call({
					method: "frappe.client.insert",
					args: { doc: newDoc },
					callback: () => {
						d.hide();
						this.init();
					},
				});
			},
		});
		d.show();
	}

	handleAddNewPrice(pl) {
		const fields = [
			{ label: __("Rate"), fieldname: "price_list_rate", fieldtype: "Currency", reqd: 1 },
			{
				label: __("Valid From"),
				fieldname: "valid_from",
				fieldtype: "Date",
				reqd: 1,
				default: frappe.datetime.get_today(),
			},
		];

		if (this.doctype !== "Item") {
			fields.unshift({
				label: __("Item"),
				fieldname: "item_code",
				fieldtype: "Link",
				options: "Item",
				reqd: 1,
			});
		} else {
			if (pl.buying)
				fields.unshift({
					label: __("Supplier"),
					fieldname: "supplier",
					fieldtype: "Link",
					options: "Supplier",
				});
			if (pl.selling)
				fields.unshift({
					label: __("Customer"),
					fieldname: "customer",
					fieldtype: "Link",
					options: "Customer",
				});
		}

		const d = new frappe.ui.Dialog({
			title: `${__("Add Price to")} ${pl.name}`,
			fields: fields,
			primary_action: (values) => {
				const doc = {
					doctype: "Item Price",
					price_list: pl.name,
					item_code: values.item_code || this.frm.doc.item_code,
					supplier: values.supplier || (this.doctype === "Supplier" ? this.frm.doc.name : null),
					customer: values.customer || (this.doctype === "Customer" ? this.frm.doc.name : null),
					price_list_rate: values.price_list_rate,
					valid_from: values.valid_from,
					buying: pl.buying,
					selling: pl.selling,
					currency: pl.currency,
				};
				frappe.call({
					method: "frappe.client.insert",
					args: { doc: doc },
					callback: () => {
						d.hide();
						this.init();
					},
				});
			},
		});
		d.show();
	}

	handleBulkAdjustment(type, plName) {
		const pricesToAdjust = this.groupedPrices[plName] || [];

		const d = new frappe.ui.Dialog({
			title: type === "increase" ? __("Bulk Increase Prices") : __("Bulk Decrease Prices"),
			fields: [
				{
					label: __("Select Prices"),
					fieldname: "selected_prices",
					fieldtype: "MultiCheck",
					options: pricesToAdjust.map((p) => ({
						label: `${p.item_code} (${format_currency(p.price_list_rate, p.currency)})`,
						value: p.name,
						checked: true,
					})),
					on_change: () => update_preview(),
				},
				{
					label: __("Percentage"),
					fieldname: "percentage",
					fieldtype: "Percent",
					reqd: 1,
					change: () => update_preview(),
				},
				{
					label: __("Preview Calculation"),
					fieldname: "preview_html",
					fieldtype: "HTML",
				},
				{ fieldtype: "Section Break" },
				{
					label: __("Valid From"),
					fieldname: "valid_from",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.add_days(frappe.datetime.get_today(), 1),
				},
			],
			primary_action_label: __("Update Prices"),
			primary_action: async (values) => {
				const selectedNames = values.selected_prices;
				if (!selectedNames.length) frappe.throw(__("Please select at least one price."));

				const factor =
					type === "increase" ? 1 + values.percentage / 100 : 1 - values.percentage / 100;
				const dayBefore = frappe.datetime.add_days(values.valid_from, -1);

				d.get_primary_btn().prop("disabled", true);

				try {
					for (const name of selectedNames) {
						const old = pricesToAdjust.find((p) => p.name === name);
						await frappe.db.set_value("Item Price", name, "valid_upto", dayBefore);

						let new_doc = { ...old };
						delete new_doc.name;
						new_doc.price_list_rate = old.price_list_rate * factor;
						new_doc.valid_from = values.valid_from;
						new_doc.valid_upto = null;
						new_doc.doctype = "Item Price";

						await frappe.call({ method: "frappe.client.insert", args: { doc: new_doc } });
					}
					d.hide();
					this.init();
					frappe.show_alert({ message: __("Prices updated"), indicator: "green" });
				} finally {
					d.get_primary_btn().prop("disabled", false);
				}
			},
		});

		const update_preview = () => {
			const val = d.get_value("percentage") || 0;
			const selected = d.get_value("selected_prices") || [];
			const sorted_prices = selected.map((id) => pricesToAdjust.find((v) => v.name == id));
			const factor = type === "increase" ? 1 + val / 100 : 1 - val / 100;

			let html = `<div class="dk-preview-container">
				<label class="control-label">${__("New Rates Preview")}</label>
				<ul class="list-unstyled">`;

			sorted_prices
				.filter((p) => selected.includes(p.name))
				.forEach((p) => {
					const new_rate = p.price_list_rate * factor;
					html += `
					<li class="dk-preview-item">
						<span>${p.item_code}&nbsp;:&nbsp;</span>
						<span class="text-muted">${format_currency(p.price_list_rate, p.currency)}</span>
						<span class="mx-2">→</span>
						<strong class="text-primary">${format_currency(new_rate, p.currency)}</strong>
					</li>`;
				});

			html += `</ul></div>`;
			d.get_field("preview_html").$wrapper.html(html);
		};

		d.show();
		update_preview();
	}

	handleBulkClose(plName) {
		const pricesToClose = this.groupedPrices[plName] || [];

		const d = new frappe.ui.Dialog({
			title: __("Bulk Close Prices"),
			fields: [
				{
					label: __("Select Prices to Close"),
					fieldname: "selected_prices",
					fieldtype: "MultiCheck",
					options: pricesToClose.map((p) => ({
						label: `${p.item_code} (${format_currency(p.price_list_rate, p.currency)})`,
						value: p.name,
						checked: true,
					})),
					columns: 1,
				},
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.get_today(),
					description: __("The selected prices will no longer be active after this date."),
				},
			],
			primary_action_label: __("Close Prices"),
			primary_action: async (values) => {
				const selectedNames = values.selected_prices;
				if (!selectedNames.length) {
					frappe.throw(__("Please select at least one price to close."));
				}

				d.get_primary_btn().prop("disabled", true);

				try {
					for (const name of selectedNames) {
						await frappe.db.set_value("Item Price", name, "valid_upto", values.valid_upto);
					}

					frappe.show_alert({ message: __("Prices closed successfully"), indicator: "orange" });
					d.hide();
					this.init();
				} catch (e) {
					console.error(e);
					frappe.msgprint(__("An error occurred while closing prices."));
				} finally {
					d.get_primary_btn().prop("disabled", false);
				}
			},
		});

		d.show();
	}
};
